import { Inject, Injectable } from '@angular/core';
import { BaseAPI } from '@core/services';
import { Mapper } from '@core/services';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { APIConstants } from '@shared/constants';
import { filterNullEntity, FunctionUtil } from '@shared/utils';
import { CustomerType, GeneralResponse } from '@shared/enums/global.enum';
import { ExportedFile } from '@shared-features/models';
import { Shipment } from '@features/shared-features/models/shipment.model';
import { DriversType } from '@shared-features/enums/fleets.enum';
import { IGetAllShipmentsPayload } from '@shared-features/interfaces/shipments-payload.interface';
import { ShipmentRequest } from '@features/shipments/models/shipment-request.model';
import { ShipmentPrices } from '@features/shipments/models/shipment-prices.model';
import { AVLSummary } from '@features/live-tracking/models/avl-summary.model';
import {
  RunningShipmentCheckType,
  ShipmentAssignedFleetsType,
  ShipmentStatus,
  TrackingType,
  TrailerLabel,
} from '@shared-features/enums/shipments.enum';
import { ShipmentRoadFromTraccar } from '@features/shipments/models/shipment-road-from-traccar.model';
import { WCCDeliveryNoteLine } from '@features/shipment-creation-template/models/wcc-delivery-note-line';
import { DateRangeType, RCustomerLookupEntityType, ShipmentDateFilterType, Status } from '@shared-features/enums/shared-enums.enum';
import { ShipmentsCount } from '@features/shipments/models/shipmentsCount.model';
import { ShipmentsModel } from '../models/shipments.model';
import { Tracking } from '@features/live-tracking/models/tracking-driver.model';
import { ShipmentStatusCount } from '../models/shipment-status-count.model';
import { DoorHistory } from '@features/fleets/models/door-history.model';
import { ShipmentOrderRoute } from '../models/shipment-order-route.model';
import { DOCUMENT } from '@angular/common';
import { UserInvoiceType } from '@shared-features/enums/user-invoice-type.enum';
import { ShipmentDto } from '../dto/shipment.dto';
import { ShipmentDeliveryNoteDto } from '../dto/shipment-delivery-note.dto';
import { ShipmentDeliveryNote } from '@features/invoices/models/shipment-delivery-note.model';
import { BidsData } from '../models/bids.model';
import { BidsSortOrder } from '../enums/bid-sort.enum';
import { BidderType } from '@features/shipment-creation-template/enums/bidder-type.enum';

@Injectable({
  providedIn: 'root',
})
export class ShipmentAPI {
  constructor(private baseAPI: BaseAPI, private mapper: Mapper, @Inject(DOCUMENT) private document: Document) {}

  private validateArray(value: any[]): any[] {
    if (!value || (Array.isArray(value) && !value.length) || (typeof value !== 'number' ? value?.includes('ALL') : false)) {
      return null;
    }
    return (Array.isArray(value) ? value : [value]).map((item) => Number(item));
  }

  private readonly ID_FILTER_KEYS = ['customerId', 'fleetId', 'creator', 'receiverId', 'fromCityId', 'toCityId'];

  private normalizeIdFields<T extends Record<string, any>>(data: T, keys: string[] = this.ID_FILTER_KEYS): T {
    return keys.reduce((acc, key) => ({ ...acc, [key]: this.validateArray(acc[key]) }) as T, data);
  }

  getShipmentById(shipmentId: string | number): Observable<Shipment> {
    return forkJoin(
      [
        APIConstants.GET_SHIPMENT_SUMMARY_BY_ID(shipmentId),
        APIConstants.GET_SHIPMENT_TRACKING_BY_ID(shipmentId),
        APIConstants.GET_SHIPMENT_REQUESTS_BY_ID(shipmentId),
        APIConstants.GET_SHIPMENT_FINANCIAL_BY_ID(shipmentId),
      ]
        .map((url) => this.baseAPI.get(url).pipe(map((res) => this.mapper.fromJson(Shipment, res.data))))
        .concat([
          this.baseAPI.get(APIConstants.GET_DELIVERY_NOTE, { params: { shipmentId } }).pipe(
            map((res) => ({ deliveryNote: this.mapper.fromJson(ShipmentDeliveryNote, res.data) })),
            catchError(() => of({ deliveryNote: [] }))
          ),
        ])
    ).pipe(map((res) => FunctionUtil.mergeIgnoringNulls<Shipment>(...res)));
  }

  assignFleet(body: {
    shipmentUuid: string;
    fleetId?: number;
    driverId: number;
    truckId: number;
    startShipment?: boolean;
    forceClose?: boolean;
    assignAndAutoStart?: boolean;
    comment?: string;
    fleetReferenceNo?: number;
    serviceType?: UserInvoiceType;
  }): Observable<GeneralResponse> {
    body['assignType'] = DriversType.Fleet;

    return this.baseAPI.post(APIConstants.API_ASSIGN_FLEET, filterNullEntity<typeof body>(body)).pipe(
      map((res) => {
        return res.data;
      })
    );
  }

  assignFleetPlanning(
    shipmentUuid: string,
    fleetId: number,
    driverId: number,
    truckId?: number,
    assignType?: DriversType,
    startShipment?: boolean,
    sendToCustomer?: boolean,
    sendToReceiver?: boolean,
    customerMobileNumber?: string,
    receiverMobileNumber?: string,
    forceClose?: boolean,
    comment?: string
  ): Observable<GeneralResponse> {
    const body = filterNullEntity({
      fleetId,
      shipmentUuid,
      assignType: assignType || DriversType.Fleet,
      driverId,
      truckId,
      startShipment,
      sendToCustomer,
      sendToReceiver,
      customerMobileNumber,
      receiverMobileNumber,
      forceClose,
      comment,
    });
    return this.baseAPI.post(APIConstants.API_ASSIGN_FLEET, body).pipe(map((res) => res.data));
  }

  assignFreelance(
    shipmentId: string,
    driverId: string,
    assignType: DriversType,
    customerPrice: number,
    fleetPrice: number
  ): Observable<GeneralResponse> {
    const body = {
      driverId: driverId,
      shipmentId: shipmentId,
      assignType: assignType,
      customerPrice,
      fleetPrice,
    };

    if (driverId) body['driverId'] = driverId;

    return this.baseAPI.post(APIConstants.API_ASSIGN_FLEET, body).pipe(
      map((res) => {
        return res.data;
      })
    );
  }

  getAllShipments(data: IGetAllShipmentsPayload): Observable<ShipmentsModel> {
    data = this.normalizeIdFields(data);

    return this.baseAPI
      .post(APIConstants.API_GET_ALL_SHIPMENTS, filterNullEntity(data))
      .pipe(map((res) => this.mapper.fromJson(ShipmentsModel, res.data)));
  }

  getAllShipmentsCount(data: IGetAllShipmentsPayload): Observable<ShipmentsCount> {
    data = this.normalizeIdFields(data);

    return this.baseAPI
      .post(APIConstants.GET_ALL_SHIPMENTS_COUNT, filterNullEntity(data))
      .pipe(map((res) => this.mapper.fromJson(ShipmentsCount, res.data)));
  }

  getUserPerformance(): Observable<ShipmentStatusCount> {
    return this.baseAPI.get(APIConstants.API_GET_USER_PERFORMANCE).pipe(
      map((res) => {
        return this.mapper.fromJson(ShipmentStatusCount, res.data);
      })
    );
  }

  addShipment(
    shipment: Shipment & { truck?: any },
    numberOfRepeats: number = 0,
    deliveryNoteIds?: number[],
    requests?: ShipmentRequest[],
    type?: RCustomerLookupEntityType,
    isOnlineBooking?: boolean
  ): Observable<Shipment> {
    const body = filterNullEntity({
      customerId: shipment.customer?.id,
      productTypeId: shipment.productType?.id,
      childCustomerId: shipment.childCustomer?.id,
      truckTypeId: shipment.truckType?.id,
      optionalTruckTypeIds: shipment.optionalTruckTypes?.map((truckType) => (truckType.id)),
      receiverId: type === RCustomerLookupEntityType.Receiver ? shipment.receiver?.id : null,
      dropOffCustomerId: type === RCustomerLookupEntityType.Customer ? shipment.receiver?.id : null,
      pickUpLocationId: shipment.pickUpLocation?.id,
      dropOffLocationId: shipment.receiverLocation?.id,
      contactPersonId: type === RCustomerLookupEntityType.Receiver ? shipment.contactPerson?.id : null,
      pickUpDate: new Date(new Date(shipment.pickedDateTime)),
      deliveryDate: new Date(new Date(shipment.deliveryDate)),
      repeatedNumber: numberOfRepeats,
      truckId: shipment.truck?.id || null,
      volume: 0,
      quantity: shipment.quantity,
      weightMeasurementUnit: shipment.weightMeasurementUnit,
      customerPreferences: shipment.customerPreferences || null,
      requests: (requests || []).map((request) =>
        filterNullEntity({
          fromType: request.fromType,
          toType: request.toType,
          toLocationId: request.toLocation?.id,
          fromLocationId: request.fromLocation?.id,
          trailerId: request.trailer?.id,
          dropOffUserId: request.dropOffUser?.id,
        })
      ),
      driverAllowance: shipment.driverAllowance,
      isOnlineBooking: isOnlineBooking,
      creationTemplateType: shipment.shipmentCreationTemplate,
      startShipment: shipment.startShipment,
      isBroadcasted: !!shipment.isBroadcasted,
      comment: shipment.comment,
      forceClose: shipment.forceClose,
      assignAndAutoStart: shipment.assignAndAutoStart,
      dynamicFields: shipment?.dynamicFields || [],
    });

    if (shipment.name) body['name'] = shipment.name;

    if (shipment.notes) body['notes'] = shipment.notes;

    if (shipment.weight) body['weight'] = shipment.weight;

    if (shipment.isFreeLanced) body['isFreeLanced'] = shipment.isFreeLanced;

    if (shipment.customerSpotRate) body['customerPrice'] = shipment.customerSpotRate;

    if (shipment.fleetSpotRate) body['fleetPrice'] = shipment.fleetSpotRate;

    if (shipment.driver?.id) {
      body['driverId'] = shipment.driver.id;
    }

    if (shipment.coDriver?.id) body['coDriverId'] = shipment.coDriver.id;

    if (shipment.priceComment) body['priceComment'] = shipment.priceComment;

    if (shipment.costComment) body['costComment'] = shipment.costComment;

    if (shipment.fleet?.id) body['fleetId'] = shipment.fleet.id;

    if (deliveryNoteIds?.length) body['deliveryNoteReferenceNumbers'] = deliveryNoteIds;

    return this.baseAPI.post(APIConstants.API_SHIPMENT_ADD, body).pipe(
      map((res) => {
        return this.mapper.fromJson(Shipment, res.data) as Shipment;
      })
    );
  }

  updateShipment(shipmentDto: Partial<ShipmentDto>): Observable<Shipment> {
    return this.baseAPI.put(APIConstants.API_SHIPMENT_UPDATE, shipmentDto).pipe(
      map((res) => {
        return this.mapper.fromJson(Shipment, res.data) as Shipment;
      })
    );
  }

  updateShipmentPrice(body: { uuid: string; customerPrice: number; fleetPrice: number; priceComment: string }): Observable<Shipment> {
    return this.baseAPI.put(APIConstants.API_SHIPMENT_UPDATE, filterNullEntity(body)).pipe(
      map((res) => {
        return this.mapper.fromJson(Shipment, res.data) as Shipment;
      })
    );
  }

  getSpotRates(
    id: number,
    fleetId: number,
    truckTypeId: number,
    fromCityId: number,
    toCityId: number,
    productTypeId?: number
  ): Observable<ShipmentPrices> {
    const params: any = {
      id,
      fleetId,
      fromCityId,
      toCityId,
      truckTypeId,
    };

    if (productTypeId) {
      params.productTypeId = productTypeId;
    }

    return this.baseAPI.get(APIConstants.API_GET_SPOT_PRICES_RATE, { params: filterNullEntity(params) }).pipe(
      map((res) => {
        return this.mapper.fromJson(ShipmentPrices, res.data);
      })
    );
  }

  updateShipmentStatus(body: { id: number; action: ShipmentStatus; comment?: string }): Observable<Shipment> {
    return this.baseAPI.put(APIConstants.API_SHIPMENT_UPDATE_STATUS, body).pipe(
      map((res) => {
        return this.mapper.fromJson(Shipment, res.data);
      })
    );
  }

  getShipmentRoad(shipmentId: number): Observable<Tracking[]> {
    return this.baseAPI.get(APIConstants.API_GET_SHIPMENT_PATH + shipmentId).pipe(map((res) => this.mapper.fromJson(Tracking, res)));
  }

  getShipmentRoadFromTracker(shipmentId: number, trackDevice?: TrackingType): Observable<ShipmentRoadFromTraccar> {
    return this.baseAPI
      .get(APIConstants.GET_SHIPMENT_AVL_PATH + shipmentId, { params: filterNullEntity({ trackDevice }) })
      .pipe(map((res) => this.mapper.fromJson(ShipmentRoadFromTraccar, res.data)));
  }

  downloadEwaybill(shipmentId: number): Observable<ExportedFile> {
    return this.baseAPI.get(APIConstants.API_DOWNLOAD_EWAYBILL + '/' + shipmentId).pipe(
      map((res) => {
        return this.mapper.fromJson(ExportedFile, res.data);
      })
    );
  }

  exportShipments(data: IGetAllShipmentsPayload & { isForExport?: boolean; creator?: number; exportedColumns }): Observable<ExportedFile> {
    data = this.normalizeIdFields(data);
    const { customerId, ...body } = data;
    return this.baseAPI.post(APIConstants.API_EXPORT_SHIPMENTS, filterNullEntity(customerId?.length ? { ...body, customerId } : body)).pipe(
      map((res) => {
        return this.mapper.fromJson(ExportedFile, res.data);
      })
    );
  }

  updateShipmentDeliveryNotesForFleet(shipment: Shipment): Observable<Shipment> {
    const body = filterNullEntity({
      id: shipment.id,
      deliveryNoteComment: shipment.deliveryNoteComment,
    });

    const deliveryNotes = [];
    shipment.deliveryNote.forEach((deliveryNote) => {
      if (deliveryNote.referenceNumber && deliveryNote.attachment?.id) {
        deliveryNotes.push({
          id: deliveryNote.id,
          referenceNumber: deliveryNote.referenceNumber,
          mediaId: deliveryNote.attachment.id,
        });
      } else if (deliveryNote.referenceNumber && !deliveryNote.attachment?.id) {
        deliveryNotes.push({
          id: deliveryNote.id,
          referenceNumber: deliveryNote.referenceNumber,
          mediaId: null,
        });
      }
    });

    (body as any).deliveryNotes = deliveryNotes;

    return this.baseAPI
      .post(APIConstants.API_UPDATE_SHIPMENT_DELIVERY_NOTES, body)
      .pipe(map((res) => this.mapper.fromJson(Shipment, res.data) as Shipment));
  }

  updateShipmentDeliveryNotesForCustomer(shipment: ShipmentDeliveryNoteDto): Observable<Shipment> {
    return this.baseAPI
      .put(APIConstants.API_SHIPMENT_UPDATE, filterNullEntity(shipment))
      .pipe(map((res) => this.mapper.fromJson(Shipment, res.data) as Shipment));
  }

  fleetAcceptShipment(body: {
    shipmentId: number;
    driverId?: number;
    truckId?: number;
    fleetReferenceNo?: number;
    startShipment?: boolean;
    forceClose?: boolean;
    assignAndAutoStart?: boolean;
    comment?: string;
  }): Observable<any> {
    return this.baseAPI.post(APIConstants.API_FLEET_ACCEPT_SHIPMENT, body);
  }

  deleteShipment(shipmentId: number): Observable<string> {
    const body = {
      id: shipmentId,
    };
    return this.baseAPI.delete(`${APIConstants.API_DELETE_SHIPMENT}${shipmentId}`, body);
  }

  getShipmentRequests(id: string): Observable<ShipmentRequest[]> {
    return this.baseAPI.get(APIConstants.GET_SHIPMENT_REQUESTS + id).pipe(map((res) => this.mapper.fromJson(ShipmentRequest, res.data)));
  }

  getAVLsummary(shipmentId: number): Observable<AVLSummary> {
    return this.baseAPI.get(APIConstants.GET_SHIPMENT_AVL_SUMMARY + `${shipmentId}`).pipe(
      map((res) => {
        return this.mapper.fromJson(AVLSummary, res.data);
      })
    );
  }

  updateTrailerLabel(body: { requestId: number; label: TrailerLabel }): Observable<string> {
    return this.baseAPI.put(APIConstants.UPDATE_TRAILER_LABEL, body).pipe(
      map((res) => {
        return this.mapper.fromJson(String, res);
      })
    );
  }

  markShipmentAsReceived(shipmentId: number) {
    return this.baseAPI.post(APIConstants.MARK_SHIPMENT_DELIVERED + `${shipmentId}`, {}).pipe(
      map((res) => {
        return this.mapper.fromJson(String, res.data);
      })
    );
  }

  getWCCDeliveryNoteLine(shipmentId: number, customerId: number): Observable<WCCDeliveryNoteLine[]> {
    return this.baseAPI.get(APIConstants.WCC_GET_DELIVERY_NOTE_LINE, { params: { shipmentId, customerId } }).pipe(
      map((res) => {
        return this.mapper.fromJson(WCCDeliveryNoteLine, res.data);
      })
    );
  }

  getShipmentsForRental(fromCityId: number, customerServiceType = CustomerType.Service): Observable<ShipmentsModel> {
    const params = filterNullEntity({
      fromCityId,
      shipmentFleetTypeFilter: ShipmentAssignedFleetsType.All,
      listForRental: true,
      status: Status.NEW,
      customerServiceType,
      dateRangeType: DateRangeType.TODAY,
      dateField: ShipmentDateFilterType.pickUpDate,
    });

    return this.baseAPI.get(APIConstants.API_GET_ALL_SHIPMENTS, { params }).pipe(
      map((res) => {
        return this.mapper.fromJson(ShipmentsModel, res.data);
      })
    );
  }

  getRunningShipments(id: number, type: RunningShipmentCheckType): Observable<Shipment> {
    return this.baseAPI.get(APIConstants.GET_RUNNING_SHIPMENTS + id, { params: { type } }).pipe(
      map((res) => {
        return this.mapper.fromJson(Shipment, res.data.shipment);
      })
    );
  }

  getDoorOpenHistory(id: number): Observable<DoorHistory[]> {
    return this.baseAPI.get(APIConstants.GET_SHIPMENT_DOOR_OPEN_HISTORY + '/' + id).pipe(
      map((res) => {
        return this.mapper.fromJson(DoorHistory, res.data);
      })
    );
  }

  getOrdersCustomers(shipmentId: number): Observable<String[]> {
    return this.baseAPI.get(APIConstants.GET_ORDERS_CUSTOMERS + shipmentId).pipe(map((res) => this.mapper.fromJson(String, res.data)));
  }

  getShipmentOrderRoute(id: number): Observable<ShipmentOrderRoute[]> {
    return this.baseAPI.get(APIConstants.GET_SHIPMENT_ORDER_ROUTE + `/${id}`).pipe(
      map((res) => {
        return this.mapper.fromJson(ShipmentOrderRoute, res.data);
      })
    );
  }

  getShipmentTrackingCoordinatesFromUrl<T = Tracking>(url: string, model?): Observable<T[]> {
    const {
      location: { protocol, hostname, port },
    } = this.document;
    const currentHostUrl = `${protocol}//${hostname}${port ? ':' + port : ''}`;
    const options = { headers: this.baseAPI.getHeaders() };
    options.headers.append('Access-Control-Allow-Origin', currentHostUrl);
    options.headers.append('Access-Control-Allow-Credentials', 'true');
    options.headers.append('Origin', currentHostUrl);

    return this.baseAPI.get(url, options).pipe(
      tap((res) => {
        return res.data;
      }),
      map((response) => {
        return this.mapper.fromJson(model || Tracking, response.coordinates);
      })
    );
  }
  getShipmentsDynamicFieldsValues(payload: { ids: number[] }): Observable<Record<number, string[]>> {
    return this.baseAPI
      .post(APIConstants.GET_DYNAMIC_FIELDS_VALUES, filterNullEntity(payload))
      .pipe(map((res) => res.data as Record<number, string[]>));
  }

  getShipmentBids(shipmentId: number, params?: { sort?: BidsSortOrder; customerPreferences?: BidderType[] }): Observable<BidsData> {
    return this.baseAPI.get(APIConstants.GET_SHIPMENT_BIDS + shipmentId, { params: filterNullEntity(params) }).pipe(
      map((res) => {
        return this.mapper.fromJson(BidsData, res.data);
      })
    );
  }

  acceptShipmentBid(bidId: number): Observable<string> {
    return this.baseAPI.post(APIConstants.ACCEPT_SHIPMENT_BID(bidId), {});
  }

  closeBiddingShipment(id: number): Observable<Shipment> {
    return this.baseAPI.post(APIConstants.CLOSE_BIDDING_SHIPMENT + id, {}).pipe(
      map((res) => this.mapper.fromJson(Shipment, res.data))
    );
  }
}


